import { useEffect, useState, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import BackLink from "@/components/BackLink";
import PendingDraftBanner from "@/components/PendingDraftBanner";
import { Plus, MapPin, Eye, EyeOff, Edit3 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function modHeaders() {
  return { Authorization: `Bearer ${sessionStorage.getItem("mod_token")}` };
}

export default function ModForeningar() {
  const [foreningar, setForeningar] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editId, setEditId] = useState(null);
  const [editForm, setEditForm] = useState({});
  const [busy, setBusy] = useState(false);
  const navigate = useNavigate();

  const fetch = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/mod/foreningar`, { headers: modHeaders() });
      setForeningar(data);
    } catch (err) {
      if (err.response?.status === 401) navigate("/mod/login");
    }
    setLoading(false);
  }, [navigate]);

  useEffect(() => {
    if (!sessionStorage.getItem("mod_token")) { navigate("/mod/login"); return; }
    fetch();
  }, [navigate, fetch]);

  const togglePublish = async (f) => {
    setBusy(true);
    const endpoint = f.status === "Publicerad" ? "unpublish" : "publish";
    await axios.post(`${API}/mod/foreningar/${f.id}/${endpoint}`, {}, { headers: modHeaders() });
    await fetch();
    setBusy(false);
  };

  const startEdit = (f) => {
    // Etappe 4.1 — defaulta andrad_av till inloggad användares namn
    const user = JSON.parse(sessionStorage.getItem("mod_user") || "null");
    setEditId(f.id);
    setEditForm({
      namn: f.namn,
      beskrivning: f.beskrivning,
      ort: f.ort || "",
      kontaktperson: f.kontaktperson || "",
      kontakt_epost: f.kontakt_epost || "",
      kontakt_telefon: f.kontakt_telefon || "",
      webbplats: f.webbplats || "",
      andrad_av: (user?.name || user?.email || "").trim(),
      andringsnotering: "",
    });
  };

  const saveEdit = async () => {
    // Etappe 4.1 — andrad_av Pflicht
    if (!editForm.andrad_av || !editForm.andrad_av.trim()) {
      alert("Ange namn på den som ändrar posten innan du sparar.");
      return;
    }
    setBusy(true);
    try {
      await axios.put(`${API}/mod/foreningar/${editId}/edit`, editForm, { headers: modHeaders() });
      setEditId(null);
      await fetch();
    } catch (err) {
      if (err.isSessionExpired) { setBusy(false); return; }
      const msg = err?.response?.data?.detail || "Kunde inte spara";
      alert(`Sparning misslyckades: ${msg}`);
    }
    setBusy(false);
  };

  return (
    <div data-testid="mod-foreningar-page" className="max-w-5xl mx-auto px-4 sm:px-6 py-8">
      <BackLink to="/mod" label="Moderatorvy" />
      <PendingDraftBanner urlPattern="/api/mod/foreningar" onSuccess={() => fetch()} />
      <div className="flex items-center justify-between mb-6">
        <h1 className="font-heading text-2xl font-medium text-ink">Föreningar</h1>
        <Link to="/mod/foreningar/skapa" data-testid="mod-add-forening" className="inline-flex items-center gap-2 bg-pine hover:bg-pine-hover text-white font-medium text-sm px-4 py-2.5 rounded-md transition-colors no-underline">
          <Plus size={16} /> Lägg till
        </Link>
      </div>

      {loading ? <p className="text-muted-foreground">Laddar...</p> : foreningar.length === 0 ? (
        <div className="bg-bone-alt rounded-sm p-8 text-center"><p className="text-muted-foreground">Inga föreningar ännu.</p></div>
      ) : (
        <div className="flex flex-col gap-3">
          {[...foreningar]
            .sort((a, b) => (a.namn || "").localeCompare(b.namn || "", "sv", { sensitivity: "base" }))
            .map((f) => (
            <div key={f.id} data-testid={`mod-forening-${f.id}`} className="bg-white border border-warm-border rounded-sm p-4">
              {editId === f.id ? (
                <div className="space-y-3">
                  <input value={editForm.namn} onChange={e => setEditForm(p => ({...p, namn: e.target.value}))} className="input-mod" placeholder="Namn" />
                  <textarea value={editForm.beskrivning} onChange={e => setEditForm(p => ({...p, beskrivning: e.target.value}))} rows={2} className="input-mod" placeholder="Beskrivning" />
                  <input value={editForm.ort} onChange={e => setEditForm(p => ({...p, ort: e.target.value}))} className="input-mod" placeholder="Ort" />
                  <input value={editForm.kontaktperson} onChange={e => setEditForm(p => ({...p, kontaktperson: e.target.value}))} className="input-mod" placeholder="Kontaktperson" />
                  <input value={editForm.kontakt_epost} onChange={e => setEditForm(p => ({...p, kontakt_epost: e.target.value}))} className="input-mod" placeholder="E-post" />
                  <input value={editForm.kontakt_telefon} onChange={e => setEditForm(p => ({...p, kontakt_telefon: e.target.value}))} className="input-mod" placeholder="Telefon" />
                  <input value={editForm.webbplats} onChange={e => setEditForm(p => ({...p, webbplats: e.target.value}))} className="input-mod" placeholder="Webbplats" />
                  {/* Etappe 4.1 — Pflicht-Audit-Feld */}
                  <div>
                    <label className="block text-[11px] font-medium text-bark/70 mb-1">Ändrad av <span className="text-falu">*</span></label>
                    <input
                      data-testid={`mod-forening-andrad-av-${f.id}`}
                      value={editForm.andrad_av || ""}
                      onChange={e => setEditForm(p => ({...p, andrad_av: e.target.value}))}
                      className="input-mod"
                      placeholder="Ange namn på den som ändrar posten"
                      required
                    />
                  </div>
                  <input
                    value={editForm.andringsnotering || ""}
                    onChange={e => setEditForm(p => ({...p, andringsnotering: e.target.value}))}
                    className="input-mod"
                    placeholder="Ändringsnotering (valfritt)"
                  />
                  <div className="flex gap-3">
                    <button onClick={saveEdit} disabled={busy} className="bg-pine hover:bg-pine-hover text-white font-medium px-4 py-2 rounded-md text-sm transition-colors">Spara</button>
                    <button onClick={() => setEditId(null)} className="text-muted-foreground text-sm">Avbryt</button>
                  </div>
                </div>
              ) : (
                <div className="flex items-start justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-1">
                      <h3 className="font-heading text-base font-medium text-ink">{f.namn}</h3>
                      <span className={`text-xs px-2 py-0.5 rounded-sm ${f.status === "Publicerad" ? "bg-green-100 text-green-800" : "bg-stone-100 text-stone-600"}`}>{f.status}</span>
                    </div>
                    {f.ort && <p className="text-sm text-muted-foreground flex items-center gap-1 mb-1"><MapPin size={12} /> {f.ort}</p>}
                    <p className="text-sm text-muted-foreground">{f.beskrivning}</p>
                  </div>
                  <div className="flex gap-2 ml-3 flex-shrink-0">
                    <button onClick={() => startEdit(f)} data-testid={`edit-${f.id}`} className="p-2 text-muted-foreground hover:text-pine transition-colors" title="Redigera"><Edit3 size={16} /></button>
                    <button onClick={() => togglePublish(f)} disabled={busy} data-testid={`toggle-${f.id}`} className="p-2 text-muted-foreground hover:text-falu transition-colors" title={f.status === "Publicerad" ? "Avpublicera" : "Publicera"}>
                      {f.status === "Publicerad" ? <EyeOff size={16} /> : <Eye size={16} />}
                    </button>
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
